"use client";

import { useEffect, useRef, useState } from "react";
import { startAuthentication, type AuthenticationResponseJSON } from "@simplewebauthn/browser";

export function PasskeyLoginButton({ username, callbackUrl }: { username?: string; callbackUrl?: string }) {
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const finishing = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function startAutofill() {
      if (!window.PublicKeyCredential) return;
      const canAutofill = await window.PublicKeyCredential.isConditionalMediationAvailable?.().catch(() => false);
      if (!canAutofill || cancelled) return;
      try {
        const assertion = await requestAssertion("", true);
        if (cancelled || finishing.current) return;
        await submitPasskeyToken(assertion, callbackUrl, finishing);
      } catch (err) {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : "";
        if (name === "AbortError" || name === "NotAllowedError") return;
      }
    }

    void startAutofill();
    return () => {
      cancelled = true;
    };
  }, [callbackUrl]);

  async function onClick() {
    setError("");
    setPending(true);
    try {
      const assertion = await requestAssertion(username?.trim() || "", false);
      await submitPasskeyToken(assertion, callbackUrl, finishing);
    } catch (err) {
      const name = err instanceof Error ? err.name : "";
      if (name === "AbortError" || name === "NotAllowedError") setError("Aanmelden geannuleerd.");
      else if (name === "SecurityError" || name === "NotSupportedError") {
        setError("Passkey werkt alleen via localhost of via de HTTPS-link, niet via een gewoon IP-adres.");
      } else setError(err instanceof Error ? err.message : "Passkey-aanmelden mislukt.");
      setPending(false);
    }
  }

  return (
    <div className="space-y-2">
      <button type="button" className="btn btn-ghost w-full min-h-12" onClick={onClick} disabled={pending}>
        {pending ? "Toestel vragen…" : "Aanmelden met passkey"}
      </button>
      {error ? <p className="text-sm text-[var(--danger)]">{error}</p> : null}
    </div>
  );
}

async function requestAssertion(username: string, useBrowserAutofill: boolean) {
  const optionsRes = await fetch("/api/passkey/auth/options", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });
  const options = await optionsRes.json();
  if (!optionsRes.ok) throw new Error(options.error || "Passkey niet beschikbaar.");
  return startAuthentication({
    optionsJSON: options,
    useBrowserAutofill,
    verifyBrowserAutofillInput: useBrowserAutofill,
  });
}

async function submitPasskeyToken(
  assertion: AuthenticationResponseJSON,
  callbackUrl: string | undefined,
  finishing: { current: boolean },
) {
  finishing.current = true;
  const verifyRes = await fetch("/api/passkey/auth/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(assertion),
  });
  const verified = await verifyRes.json();
  if (!verifyRes.ok || !verified.token) {
    finishing.current = false;
    throw new Error(verified.error || "Passkey geweigerd.");
  }
  const form = document.createElement("form");
  form.method = "POST";
  form.action = "/api/passkey/login";
  form.style.display = "none";
  const token = document.createElement("input");
  token.name = "passkeyToken";
  token.value = String(verified.token);
  form.appendChild(token);
  if (callbackUrl) {
    const next = document.createElement("input");
    next.name = "callbackUrl";
    next.value = callbackUrl;
    form.appendChild(next);
  }
  document.body.appendChild(form);
  form.submit();
}
